import {parse} from 'csv-parse/browser/esm/sync';
import {TEMPLATE_COLUMNS, STANDARD_MAPPING_FIELDS, HEADER_ALIASES} from './types';
import type {ValidationIssue} from './types';

export function stripBom(s: string): string {
  return s.replace(/^\uFEFF/, '');
}

export function resolveSystemField(csvHeader: string): string {
  const normalized = stripBom(csvHeader).toLowerCase().trim().replace(/[_\-]+/g, ' ');
  const exactMatch = STANDARD_MAPPING_FIELDS.find((f) => f.toLowerCase() === normalized);
  if (exactMatch) return exactMatch;
  return HEADER_ALIASES[normalized] ?? '';
}

export async function parseCSVFile(
  file: File,
): Promise<{headers: string[]; rows: string[][]; totalRows: number; rawRowCount: number}> {
  const content = await file.text();

  // Let parse errors propagate so the caller can surface the real reason to
  // the user instead of silently returning an empty grid.
  const rawRows: string[][] = parse(content, {
    columns: false,
    skip_empty_lines: true,
    trim: true,
    relax_column_count: true,
    bom: true,
  });

  if (rawRows.length === 0) {
    return {headers: [], rows: [], totalRows: 0, rawRowCount: 0};
  }

  const rawRowCount = rawRows.length - 1;
  const headers = rawRows[0].map((h) => stripBom((h ?? '').trim()));
  const rows = rawRows.slice(1).filter((r) => r.some((cell) => cell?.trim()));
  return {headers, rows, totalRows: rows.length, rawRowCount};
}

export function validateRows(
  headers: string[],
  rows: string[][],
): {errors: ValidationIssue[]; warnings: ValidationIssue[]} {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.length !== headers.length) {
      warnings.push({
        row: i + 2,
        message: `Column count mismatch: expected ${headers.length}, got ${row.length}`,
        severity: 'warning',
      });
    }
    if (row.every((cell) => !cell.trim())) {
      warnings.push({row: i + 2, message: 'Empty row', severity: 'warning'});
    }
  }

  return {errors, warnings};
}

export function findDuplicates(
  rows: string[][],
  headers: string[],
): {indices: number[]; key: string}[] {
  const ssnIdx = headers.findIndex((h) => h.toLowerCase().includes('ssn'));
  if (ssnIdx === -1) return [];

  const seen = new Map<string, number[]>();
  for (let i = 0; i < rows.length; i++) {
    const val = rows[i][ssnIdx]?.trim();
    if (!val) continue;
    const existing = seen.get(val);
    if (existing) {
      existing.push(i + 2);
    } else {
      seen.set(val, [i + 2]);
    }
  }

  return Array.from(seen.entries())
    .filter(([, indices]) => indices.length > 1)
    .map(([key, indices]) => ({key, indices}));
}

export function downloadTemplate() {
  const header = TEMPLATE_COLUMNS.join(',') + '\n';
  const blob = new Blob([header], {type: 'text/csv'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'member-import-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}
